import { useCallback, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Button, Icon, IconButton, Modal, Progress, useToast } from "@astrum/ui";
import { broadcastMentions, resolveMentions, stripMarkdown } from "@astrum/shared";
import type { Channel } from "../../lib/api.js";
import { formatBytes, iconForType } from "../../components/AttachmentList.js";
import { EmojiPickerPopover } from "../../components/EmojiPickerPopover.js";
import { discardDraft, getDraft, saveDraft } from "../../lib/drafts.js";
import { usePersistentState } from "../../lib/use-persistent-state.js";
import { MessageInput } from "./MessageInput.js";
import type { MessageInputHandle } from "./MessageInput.js";
import type { ChannelMember, PendingMessage, UploadingFile } from "./types.js";
import type { FileUploads } from "./useFileUploads.js";

/**
 * Above this many members, `@channel` and `@here` ask before sending — and
 * `@channel` is for the channel's owners and admins, as the server enforces.
 * Mirrors BROADCAST_MEMBER_LIMIT in the API.
 */
export const BROADCAST_CONFIRM_ABOVE = 20;

const PASTED_IMAGE_EXTENSION: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
};

/**
 * A screenshot copied to the clipboard arrives with a generic name like
 * `image.png` (or none at all) — no author ever typed one, so there is
 * nothing worth keeping. Named after the moment it was pasted instead, the
 * same convention macOS and Windows use for a screenshot saved to disk, so
 * it reads as a real file rather than a placeholder in the past-reports
 * list and any later download.
 */
function namePastedImage(file: File): File {
  if (file.name && file.name !== "image.png" && !/^blob$/i.test(file.name)) return file;
  const ext = PASTED_IMAGE_EXTENSION[file.type] ?? "png";
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return new File([file], `Pasted image ${stamp}.${ext}`, { type: file.type, lastModified: file.lastModified });
}

/**
 * The optimistic "this file is uploading" row shown above the composer, one per
 * file, with a live progress bar. Turns into a failed state (with a dismiss) on
 * error; otherwise it disappears when the real message arrives in the thread.
 */
function UploadBubble({ upload, onDismiss }: { upload: UploadingFile; onDismiss: () => void }) {
  const done = upload.progress >= 1 && !upload.failed;
  return (
    <div className={`chat-upload ${upload.failed ? "is-failed" : ""}`}>
      <Icon name={upload.failed ? "warning" : iconForType(upload.fileName)} size={16} />
      <div className="chat-upload__info">
        <div className="chat-upload__name">
          <span className="chat-upload__filename">{upload.fileName}</span>
          <span className="chat-upload__size">{formatBytes(upload.sizeBytes)}</span>
        </div>
        {upload.failed ? (
          <span className="chat-upload__status is-failed">{upload.error ?? "Upload failed"}</span>
        ) : done ? (
          // Body fully sent; the server is scanning/storing. Indeterminate tail.
          <span className="chat-upload__status">Finishing…</span>
        ) : (
          <Progress value={upload.progress} label={`Uploading ${upload.fileName}`} compact />
        )}
      </div>
      <IconButton
        icon={<Icon name="close" size={13} />}
        label={upload.failed ? `Dismiss ${upload.fileName}` : `Cancel uploading ${upload.fileName}`}
        size="sm"
        variant="ghost"
        onClick={onDismiss}
      />
    </div>
  );
}

interface ComposerProps {
  channelKind: Channel["kind"] | undefined;
  placeholder: string;
  /**
   * Hands over a finished message, mentions already in wire form. The send
   * itself — optimistic row, retry queue, failed state — belongs to
   * useChannelMessages, so the composer never sees a message after this.
   */
  onSend: (body: string) => void;
  onTyping: () => void;
  onCreateTask: () => void;
  onCreateEvent: () => void;
  /** Files being shared: the bubbles above the field, and where a picked or pasted file goes. */
  files: FileUploads;
  /** The message being replied to, shown above the field. */
  replyingTo: PendingMessage | null;
  onCancelReply: () => void;
  /**
   * Who can be named here.
   *
   * The channel's members, not the whole workspace: mentioning somebody who
   * cannot read the channel would be a notification about a conversation they
   * have no access to, and the server refuses it anyway.
   */
  members: ChannelMember[];
  /** Extra controls above the toolbar, e.g. a thread's "Also send to channel". */
  footer?: ReactNode;
  /**
   * Whether files can be attached here. Off in a thread: an upload is posted
   * to the channel stream, so offering it in a thread would put the file
   * somewhere other than where the person was typing.
   */
  allowFiles?: boolean;
  /** Whether the viewer may use `@channel` in a large channel: an owner, the creator, or an admin. */
  canBroadcast?: boolean;
  /**
   * Where unsent text is kept. Without it the composer has no draft; with it,
   * what was typed here before is restored on mount and saved as it changes.
   */
  draft?: { channelId: string; threadRootId: string | null };
}

export function Composer({
  channelKind,
  placeholder,
  onSend,
  onTyping,
  onCreateTask,
  onCreateEvent,
  files,
  replyingTo,
  onCancelReply,
  members,
  footer,
  allowFiles = true,
  canBroadcast = false,
  draft,
}: ComposerProps) {
  const { toast } = useToast();
  const [value, setValue] = useState(() => (draft ? getDraft(draft.channelId, draft.threadRootId) : ""));
  const draftChannelId = draft?.channelId;
  const draftThreadRootId = draft?.threadRootId ?? null;
  const updateValue = useCallback(
    (next: string) => {
      setValue(next);
      if (draftChannelId) saveDraft(draftChannelId, draftThreadRootId, next);
    },
    [draftChannelId, draftThreadRootId],
  );
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [toolbarPref, setToolbarPref] = usePersistentState("chat.composer-toolbar", "off", ["on", "off"] as const);
  const showToolbar = toolbarPref === "on";
  /* A large @channel/@here waiting on "are you sure". */
  const [confirming, setConfirming] = useState<{ body: string; kind: "channel" | "here" } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef<MessageInputHandle | null>(null);
  const isProjectChannel = channelKind === "project";
  const { uploads, attaching, attach, dismiss } = files;

  const send = useCallback(
    (resolved: string) => {
      onSend(resolved);
      setValue("");
      if (draftChannelId) discardDraft(draftChannelId, draftThreadRootId);
      setConfirming(null);
    },
    [onSend, draftChannelId, draftThreadRootId],
  );

  const submit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed) return;
    // The textarea holds readable `@Name`; the wire form is built here, at the
    // last moment, against the people who could actually be named in this
    // channel. So the message stored still records who was mentioned by id.
    const resolved = resolveMentions(
      trimmed,
      members.map((m) => ({ id: m.id, displayName: m.displayName })),
    );
    // Notifying a whole large channel is worth one more click — and in one
    // this size `@channel` is for its owners, which the server enforces too.
    const broadcast = broadcastMentions(resolved);
    if ((broadcast.channel || broadcast.here) && members.length > BROADCAST_CONFIRM_ABOVE) {
      if (broadcast.channel && !canBroadcast) {
        toast(`Only the channel's owners and admins can use @channel with more than ${BROADCAST_CONFIRM_ABOVE} members. Try @here.`, {
          tone: "danger",
        });
        return;
      }
      setConfirming({ body: resolved, kind: broadcast.channel ? "channel" : "here" });
      return;
    }
    send(resolved);
  }, [value, members, canBroadcast, send, toast]);

  return (
    <>
      {uploads.length > 0 && (
        <div className="chat-uploads">
          {uploads.map((u) => (
            <UploadBubble key={u.clientId} upload={u} onDismiss={() => dismiss(u)} />
          ))}
        </div>
      )}

      <div className="chat-composer">
        {/* What you are replying to, with a way out. Without the dismiss the only
            escape from a mis-clicked reply would be to send it. */}
        {replyingTo && (
          <div className="chat-composer__reply">
            <Icon name="reply" size={13} />
            <div className="chat-composer__reply-text">
              <span className="chat-composer__reply-author">Replying to {replyingTo.authorName}</span>
              <span className="chat-composer__reply-body">{stripMarkdown(replyingTo.body)}</span>
            </div>
            <IconButton
              icon={<Icon name="close" size={13} />}
              label="Cancel reply"
              size="sm"
              variant="ghost"
              onClick={onCancelReply}
            />
          </div>
        )}

        <MessageInput
          value={value}
          onChange={updateValue}
          onSubmit={submit}
          placeholder={placeholder}
          members={members}
          onTyping={onTyping}
          onPasteFiles={(pasted) => {
            if (allowFiles) pasted.forEach((file) => attach(namePastedImage(file)));
          }}
          showToolbar={showToolbar}
          handleRef={inputRef}
        />
        {footer}
        <div className="chat-composer__bar">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            aria-label="Attach files"
            className="chat-composer__file-input"
            onChange={(e) => {
              Array.from(e.target.files ?? []).forEach((file) => attach(file));
              e.target.value = "";
            }}
          />
          {allowFiles && (
            <IconButton
              icon={<Icon name={attaching ? "spinner" : "attach"} size={15} />}
              label={attaching ? "Uploading…" : "Attach a file"}
              size="sm"
              disabled={attaching}
              onClick={() => fileInputRef.current?.click()}
            />
          )}

          <EmojiPickerPopover
            open={emojiOpen}
            onClose={() => setEmojiOpen(false)}
            label="Insert emoji"
            onPick={(emoji) => {
              inputRef.current?.insert(emoji);
              setEmojiOpen(false);
            }}
            trigger={
              <IconButton
                icon={<Icon name="emoji" size={15} />}
                label="Add emoji"
                size="sm"
                variant={emojiOpen ? "soft" : "ghost"}
                onClick={() => setEmojiOpen((v) => !v)}
              />
            }
          />
          <Button
            variant={showToolbar ? "secondary" : "ghost"}
            size="sm"
            aria-pressed={showToolbar}
            aria-label={showToolbar ? "Hide formatting" : "Show formatting"}
            title={showToolbar ? "Hide formatting" : "Show formatting"}
            onClick={() => setToolbarPref(showToolbar ? "off" : "on")}
          >
            Aa
          </Button>

          {/* Only in a project channel: a task or event raised from a DM has no
              project to belong to, and offering it there produces orphans. */}
          {isProjectChannel && (
            <>
              <Button variant="ghost" size="sm" icon={<Icon name="check" size={14} />} onClick={onCreateTask}>
                Task
              </Button>
              <Button variant="ghost" size="sm" icon={<Icon name="calendar" size={14} />} onClick={onCreateEvent}>
                Event
              </Button>
            </>
          )}

          <div className="chat-composer__spacer" />
          <IconButton
            icon={<Icon name="send" size={15} />}
            label="Send message"
            variant="solid"
            onClick={submit}
            disabled={!value.trim()}
          />
        </div>
      </div>

      <Modal
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={confirming?.kind === "channel" ? "Notify everyone?" : "Notify everyone online?"}
      >
        <p className="chat-composer__confirm-text">
          {confirming?.kind === "channel"
            ? `@channel notifies all ${members.length - 1} other members of this conversation.`
            : `@here notifies everyone in this conversation who is online — up to ${members.length - 1} people.`}
        </p>
        <div className="chat-composer__confirm-actions">
          <Button variant="secondary" onClick={() => setConfirming(null)}>
            Cancel
          </Button>
          <Button onClick={() => confirming && send(confirming.body)}>Send</Button>
        </div>
      </Modal>
    </>
  );
}
